import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  CloseButton,
  HStack,
  Box,
  useColorModeValue,
  Fade,
  Icon,
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import {
  hasNotificationPermission,
  hasAskedForNotificationPermission,
  initializeFCM,
} from '../../utils/messaging';

export default function NotificationPermissionBanner() {
  const { currentUser } = useAuth();
  const [show, setShow] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const bgColor = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('blue.200', 'blue.700');

  useEffect(() => {
    // Show banner if:
    // 1. User is authenticated
    // 2. Notifications are supported
    // 3. Permission not granted
    // 4. Haven't asked before
    if (
      currentUser &&
      'Notification' in window &&
      !hasNotificationPermission() &&
      !hasAskedForNotificationPermission()
    ) {
      // Show after a brief delay so it doesn't appear immediately on page load
      const timer = setTimeout(() => {
        setShow(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [currentUser]);

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      const result = await initializeFCM(currentUser.uid);
      
      if (result.success) {
        setShow(false);
      } else {
        console.error('Failed to enable notifications:', result.error);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    // Don't mark as asked so it shows again next session
  };

  const handleNotNow = () => {
    setShow(false);
    // Mark as asked so we don't show again
    localStorage.setItem('notificationPermissionAsked', 'true');
  };

  if (!show) {
    return null;
  }

  return (
    <Fade in={show}>
      <Box mb={4}>
        <Alert
          status="info"
          variant="left-accent"
          borderRadius="lg"
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          py={4}
        >
          <AlertIcon as={FiBell} />
          <Box flex="1">
            <AlertTitle fontSize="md" mb={1}>
              Enable Push Notifications
            </AlertTitle>
            <AlertDescription fontSize="sm" display="block">
              Get notified about at-risk deals, new lead assignments, and important updates.
              You can change this anytime in your settings.
            </AlertDescription>
          </Box>
          <HStack spacing={2}>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleEnable}
              isLoading={isEnabling}
              loadingText="Enabling..."
            >
              Enable
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNotNow}
            >
              Not Now
            </Button>
            <CloseButton
              alignSelf="flex-start"
              position="relative"
              right={-1}
              top={-1}
              onClick={handleDismiss}
            />
          </HStack>
        </Alert>
      </Box>
    </Fade>
  );
}

